import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Badge,
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import {
  getChatbotSettings,
  updateChatbotSettings,
} from "store/actions/chatbotKnowledgeAction";

const ChatbotSettingsModal = ({ modal, toggle }) => {
  const dispatch = useDispatch();
  const { settings, settingsLoader, saveSettingsLoader } = useSelector(
    (state) => state.chatbotKnowledge
  );

  const [formData, setFormData] = useState({
    adminEmail: "",
    supportEmail: "",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (modal) {
      dispatch(getChatbotSettings());
    }
  }, [modal, dispatch]);

  useEffect(() => {
    if (settings) {
      setFormData({
        adminEmail: settings.adminEmail || "",
        supportEmail: settings.supportEmail || "",
      });
    }
  }, [settings]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (formData.adminEmail && !emailRegex.test(formData.adminEmail.trim())) {
      newErrors.adminEmail = "Please enter a valid email address";
    }

    if (formData.supportEmail && !emailRegex.test(formData.supportEmail.trim())) {
      newErrors.supportEmail = "Please enter a valid email address";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (!validate()) return;

    dispatch(
      updateChatbotSettings(
        {
          adminEmail: formData.adminEmail ? formData.adminEmail.trim() : null,
          supportEmail: formData.supportEmail ? formData.supportEmail.trim() : null,
        },
        () => {
          toggle();
        }
      )
    );
  };

  const activeRecipients =
    settings?.escalationEmails && settings.escalationEmails.length > 0
      ? settings.escalationEmails
      : [settings?.adminEmail, settings?.supportEmail].filter(Boolean);

  return (
    <Modal isOpen={modal} toggle={toggle} centered size="lg" scrollable={true}>
      <ModalHeader toggle={toggle} className="border-bottom">
        <i className="ni ni-settings-gear-65 mr-2 text-primary" />
        Chatbot Escalation Settings
      </ModalHeader>

      <ModalBody className="py-3">
        <div
          className="d-flex align-items-start mb-3 p-3 rounded"
          style={{
            backgroundColor: "#f0f7f7",
            border: "1px solid #d0e7e5",
            borderLeft: "4px solid #008080",
          }}
        >
          <i
            className="fas fa-info-circle mr-3 mt-1"
            style={{ color: "#008080", fontSize: "16px", flexShrink: 0 }}
          />
          <div style={{ fontSize: "13px", color: "#334e68", lineHeight: "1.5" }}>
            <span style={{ fontWeight: "600", color: "#005c5c" }}>
              Guest Escalations:{" "}
            </span>
            When a logged-out or guest visitor requests human assistance, their chat
            transcript and contact details are emailed to these addresses. Logged-in
            users are automatically routed to Support Tickets.
          </div>
        </div>

        {settingsLoader ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
            <p className="mt-2 text-muted mb-0 small">Loading settings...</p>
          </div>
        ) : (
          <Row>
            <Col md="12" className="my-2">
              <FormGroup>
                <Label className="form-control-label font-weight-bold" htmlFor="adminEmailModal">
                  Admin Escalation Email
                </Label>
                <Input
                  id="adminEmailModal"
                  name="adminEmail"
                  type="email"
                  placeholder="e.g. admin@tealpot.com"
                  value={formData.adminEmail}
                  onChange={handleChange}
                  invalid={!!errors.adminEmail}
                />
                {errors.adminEmail && (
                  <div className="text-danger text-xs mt-1">{errors.adminEmail}</div>
                )}
                <small className="text-muted">
                  Primary administrator address receiving escalation notices.
                </small>
              </FormGroup>
            </Col>

            <Col md="12" className="my-2">
              <FormGroup>
                <Label className="form-control-label font-weight-bold" htmlFor="supportEmailModal">
                  Support Escalation Email
                </Label>
                <Input
                  id="supportEmailModal"
                  name="supportEmail"
                  type="email"
                  placeholder="e.g. support@tealpot.com"
                  value={formData.supportEmail}
                  onChange={handleChange}
                  invalid={!!errors.supportEmail}
                />
                {errors.supportEmail && (
                  <div className="text-danger text-xs mt-1">{errors.supportEmail}</div>
                )}
                <small className="text-muted">
                  Customer support inbox for agent ticket follow-up.
                </small>
              </FormGroup>
            </Col>

            {activeRecipients.length > 0 && (
              <Col md="12" className="mt-2">
                <div
                  className="p-3 rounded"
                  style={{
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #e9ecef",
                  }}
                >
                  <small className="font-weight-bold text-dark d-block mb-2">
                    <i className="fas fa-check-circle text-success mr-1" />
                    Current Active Escalation Recipients:
                  </small>
                  <div className="d-flex flex-wrap align-items-center">
                    {activeRecipients.map((email, idx) => (
                      <span
                        key={idx}
                        className="badge badge-pill mr-2 mb-1 px-3 py-2 d-inline-flex align-items-center"
                        style={{
                          backgroundColor: "#e8f5f4",
                          color: "#006666",
                          fontSize: "12px",
                          fontWeight: "500",
                          textTransform: "none",
                          border: "1px solid #b2dfdb",
                          letterSpacing: "normal",
                        }}
                      >
                        <i className="fas fa-envelope mr-1 text-muted" style={{ fontSize: "11px" }} />
                        {email}
                      </span>
                    ))}
                  </div>
                </div>
              </Col>
            )}
          </Row>
        )}
      </ModalBody>

      <ModalFooter className="border-top">
        <Button color="secondary" onClick={toggle} disabled={saveSettingsLoader}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={saveSettingsLoader || settingsLoader}
          style={{ backgroundColor: "#008080", borderColor: "#008080" }}
        >
          {saveSettingsLoader ? (
            <>
              <Spinner size="sm" className="mr-1" /> Saving...
            </>
          ) : (
            <>
              <i className="fas fa-save mr-1" /> Save Settings
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ChatbotSettingsModal;
